/**
 * OAuth 2.0 / OIDC wire contract (PRD §4.2, §10, §15).
 *
 * Shared between the auth server, the portal, and — via the published package —
 * the module clients, so the three cannot drift on parameter names or error
 * codes. Everything here follows RFC 6749 (OAuth 2.0), RFC 7636 (PKCE) and
 * OpenID Connect Core, because "OIDC-compatible" is only useful to a module
 * team if a standard client library can talk to it unmodified.
 */
import { z } from 'zod';

/* ------------------------------------------------------------------ *
 * Primitives
 * ------------------------------------------------------------------ */

/**
 * Client identifier, e.g. `meridian`. Constrained to a slug so it is safe to
 * interpolate into log lines, URLs and page copy without escaping surprises.
 */
export const clientIdSchema = z
  .string()
  .trim()
  .min(1, 'client_id is required')
  .max(64)
  .regex(/^[a-z0-9][a-z0-9-]*$/, 'client_id must be a lowercase slug');

/**
 * Absolute callback URL. Validated for shape only — whether it is *permitted*
 * is decided by exact-matching it against the application's registered list
 * (PRD §9, §25.10), which is the check that actually prevents open redirects.
 *
 * http is tolerated for `localhost` so module teams can develop locally.
 */
export const redirectUriSchema = z
  .string()
  .trim()
  .min(1, 'redirect_uri is required')
  .max(2048)
  .refine((value) => {
    let url: URL;
    try {
      url = new URL(value);
    } catch {
      return false;
    }
    if (url.hash !== '') return false; // A fragment cannot survive the redirect.
    if (url.protocol === 'https:') return true;
    return url.protocol === 'http:' && (url.hostname === 'localhost' || url.hostname === '127.0.0.1');
  }, 'redirect_uri must be an absolute https URL (or http://localhost in development)');

/**
 * Opaque module-owned value, echoed back untouched. Required, not optional:
 * without it the module cannot detect CSRF on its own callback (PRD §16).
 *
 * The module's original in-app destination belongs in here — encoded inside
 * state the module itself controls — rather than in a `returnTo` parameter this
 * service would have to trust (PRD §16 "any internal destination must be a
 * relative path or encoded inside module-controlled state").
 */
export const stateSchema = z
  .string()
  .trim()
  .min(8, 'state must be at least 8 characters')
  .max(1024);

/** PKCE verifier/challenge lengths are fixed by RFC 7636 §4.1. */
export const codeChallengeSchema = z
  .string()
  .trim()
  .min(43)
  .max(128)
  .regex(/^[A-Za-z0-9\-._~]+$/, 'code_challenge must be base64url');

export const codeVerifierSchema = z
  .string()
  .trim()
  .min(43)
  .max(128)
  .regex(/^[A-Za-z0-9\-._~]+$/, 'code_verifier must be base64url');

/** Space-delimited scope list, per RFC 6749 §3.3. */
const scopeStringSchema = z
  .string()
  .trim()
  .max(512)
  .transform((value) => value.split(/\s+/).filter((entry) => entry.length > 0));

export const SUPPORTED_SCOPES = ['openid', 'profile', 'email', 'organization', 'offline_access'] as const;
export type SupportedScope = (typeof SUPPORTED_SCOPES)[number];

/** Scopes granted when a module asks for none. */
export const DEFAULT_SCOPES: readonly SupportedScope[] = ['openid', 'profile', 'email', 'organization'];

/**
 * OIDC `prompt` values we honour.
 *  - `none`: never show UI; fail with `login_required` instead. Used for silent
 *    SSO probes from a module that wants to know if a session exists.
 *  - `login`: force re-authentication even with a live session.
 *  - `select_account`: always show the organization chooser.
 */
export const PROMPT_VALUES = ['none', 'login', 'select_account'] as const;
export type Prompt = (typeof PROMPT_VALUES)[number];

/* ------------------------------------------------------------------ *
 * /authorize
 * ------------------------------------------------------------------ */

/**
 * Authorization request parameters (PRD §4.2).
 *
 * PKCE is mandatory — including for confidential clients. The PRD calls for it
 * on first-party apps regardless (§25.4), and there is no downside to a client
 * that already holds a secret also proving possession of a verifier.
 */
export const authorizeParamsSchema = z.object({
  client_id: clientIdSchema,
  redirect_uri: redirectUriSchema,
  response_type: z.literal('code', {
    errorMap: () => ({ message: 'Only response_type=code is supported' }),
  }),
  state: stateSchema,
  code_challenge: codeChallengeSchema,
  code_challenge_method: z.literal('S256', {
    errorMap: () => ({ message: 'Only code_challenge_method=S256 is supported' }),
  }),
  scope: scopeStringSchema.optional(),
  /** Bound into the id token so the module can detect token replay. */
  nonce: z.string().trim().min(8).max(512).optional(),
  prompt: z.enum(PROMPT_VALUES).optional(),
  /** Pre-fills the sign-in form; never trusted as proof of identity. */
  login_hint: z.string().trim().max(254).optional(),
  /**
   * Requested active organization. Validated against the user's live
   * memberships — a client-side identifier alone never selects an org
   * (PRD §16 "tenant isolation").
   */
  organization_id: z.string().trim().min(1).max(128).optional(),
});
export type AuthorizeParams = z.infer<typeof authorizeParamsSchema>;

/**
 * Resuming an interrupted authorization.
 *
 * The portal only ever carries this opaque handle, so OAuth parameters are
 * validated once, at the point of entry, and cannot be tampered with while the
 * user is signing in.
 */
export const authRequestIdSchema = z
  .string()
  .trim()
  .min(8)
  .max(128)
  .regex(/^[A-Za-z0-9_-]+$/, 'Invalid authorization request');

/** Query parameter name for the handle above. */
export const AUTH_REQUEST_PARAM = 'auth_request';

/* ------------------------------------------------------------------ *
 * /token
 * ------------------------------------------------------------------ */

export const authorizationCodeGrantSchema = z.object({
  grant_type: z.literal('authorization_code'),
  code: z.string().trim().min(16).max(512),
  /** Must equal the URI the code was issued for (RFC 6749 §4.1.3). */
  redirect_uri: redirectUriSchema,
  client_id: clientIdSchema,
  code_verifier: codeVerifierSchema,
  /** Confidential clients may authenticate in the body (§9). */
  client_secret: z.string().trim().min(16).max(512).optional(),
});

export const refreshTokenGrantSchema = z.object({
  grant_type: z.literal('refresh_token'),
  refresh_token: z.string().trim().min(16).max(512),
  client_id: clientIdSchema,
  client_secret: z.string().trim().min(16).max(512).optional(),
  /** Narrowing only: a refresh may not acquire scopes the code did not grant. */
  scope: scopeStringSchema.optional(),
});

export const tokenRequestSchema = z.discriminatedUnion('grant_type', [
  authorizationCodeGrantSchema,
  refreshTokenGrantSchema,
]);
export type TokenRequest = z.infer<typeof tokenRequestSchema>;

/** Successful token response (RFC 6749 §5.1, OIDC Core §3.1.3.3). */
export interface TokenResponse {
  access_token: string;
  token_type: 'Bearer';
  expires_in: number;
  scope: string;
  id_token?: string;
  refresh_token?: string;
}

/* ------------------------------------------------------------------ *
 * Errors
 * ------------------------------------------------------------------ */

/**
 * Errors returned to the module by redirect (RFC 6749 §4.1.2.1, OIDC §3.1.2.6).
 *
 * Access *denials* are deliberately absent: when a user's organization lacks an
 * entitlement, the user stays on auth.mindpeer.com and reads an explanation
 * (PRD §8.3). Redirecting `error=access_denied` to the module instead would
 * bounce them into a loop with nothing to act on.
 */
export const AUTHORIZE_ERROR = [
  'invalid_request',
  'unauthorized_client',
  'access_denied',
  'unsupported_response_type',
  'invalid_scope',
  'server_error',
  'temporarily_unavailable',
  'login_required',
  'interaction_required',
  'account_selection_required',
] as const;
export type AuthorizeError = (typeof AUTHORIZE_ERROR)[number];

/** Errors returned in the body of a failed token request (RFC 6749 §5.2). */
export const TOKEN_ERROR = [
  'invalid_request',
  'invalid_client',
  'invalid_grant',
  'unauthorized_client',
  'unsupported_grant_type',
  'invalid_scope',
  'server_error',
] as const;
export type TokenError = (typeof TOKEN_ERROR)[number];

export interface OAuthErrorBody {
  error: string;
  error_description?: string;
}

/* ------------------------------------------------------------------ *
 * /logout
 * ------------------------------------------------------------------ */

export const logoutParamsSchema = z.object({
  client_id: clientIdSchema.optional(),
  post_logout_redirect_uri: z.string().trim().max(2048).optional(),
  state: z.string().trim().max(1024).optional(),
  /** Ends every session for the user, not just this browser (PRD §11.4). */
  global: z.enum(['true', 'false']).optional(),
});
export type LogoutParams = z.infer<typeof logoutParamsSchema>;

/* ------------------------------------------------------------------ *
 * Token claims
 * ------------------------------------------------------------------ */

/**
 * Access-token claims (PRD §11.1).
 *
 * Deliberately minimal — "only the minimum information required by the module".
 * Fine-grained permissions stay inside each module (§3), so no permission list
 * appears here.
 */
export interface AccessTokenClaims {
  iss: string;
  sub: string;
  aud: string;
  exp: number;
  iat: number;
  jti: string;
  email: string;
  email_verified: boolean;
  org_id: string;
  org_slug: string;
  /**
   * Display name of the active organization. `org_id` is the identifier a
   * module must key its own tenant record on; this is only for showing the
   * customer their own organization's name the first time a module sees them,
   * so it must never be used to resolve or match a tenant.
   */
  org_name: string;
  /** Roles on the active membership — `ORGANIZATION_ROLE`, never platform roles. */
  roles: string[];
  /**
   * Platform-wide roles (`PLATFORM_ROLE`), independent of any organization.
   * Kept separate from `roles` because the two vocabularies overlap in the
   * worst possible way: an organization's own `admin` must never be read as
   * platform `admin`, which would hand a customer's admin every tenant on the
   * platform. Modules deriving an "is staff" flag must read this claim only.
   */
  platform_roles: string[];
  application: string;
  scope: string;
  /** Central session this token descends from, for correlation and revocation. */
  sid: string;
}

/** Id-token claims (OIDC Core §2). `aud` is the client, not the API. */
export interface IdTokenClaims {
  iss: string;
  sub: string;
  aud: string;
  exp: number;
  iat: number;
  auth_time: number;
  nonce?: string;
  email: string;
  email_verified: boolean;
  name: string;
  given_name: string;
  family_name: string;
  org_id: string;
  org_slug: string;
  /** Display only — see the note on `AccessTokenClaims.org_name`. */
  org_name: string;
  /** Roles on the active membership — `ORGANIZATION_ROLE`, never platform roles. */
  roles: string[];
  /** Platform-wide roles — see the note on `AccessTokenClaims.platform_roles`. */
  platform_roles: string[];
  sid: string;
}

/* ------------------------------------------------------------------ *
 * Portal-facing payloads
 * ------------------------------------------------------------------ */

/**
 * The safe, public subset of an application registration.
 *
 * Lets the sign-in screen say "Continue to Meridian" with the module's own
 * branding. Crucially the name and logo come from our registry rather than from
 * the query string, so a crafted link cannot make the login page impersonate a
 * module the user does not have (PRD §16 redirect protection, in spirit).
 */
export const applicationSummarySchema = z.object({
  clientId: z.string(),
  name: z.string(),
  logoUrl: z.string().nullable(),
});
export type ApplicationSummary = z.infer<typeof applicationSummarySchema>;

/** `GET /api/auth-request/:id` — what the portal needs to render the resume. */
export const authRequestSummarySchema = z.object({
  id: z.string(),
  application: applicationSummarySchema,
  /** Pre-fill for the email field, when the module supplied a `login_hint`. */
  loginHint: z.string().nullable(),
  /** True when the module asked for the organization chooser explicitly. */
  requiresOrganizationSelection: z.boolean(),
});
export type AuthRequestSummary = z.infer<typeof authRequestSummarySchema>;
